import { motion, AnimatePresence } from "framer-motion";import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV } from "@/constants/testIds";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Lets the Contact section know whether it was opened via "Contact" (Say Hello)
// or "Begin a Project", so it can show the right heading/copy.
const announceContactIntent = (intent) => {
  sessionStorage.setItem("hd-contact-intent", intent);
  window.dispatchEvent(new CustomEvent("hankdavid-contact-intent", { detail: intent }));
};

// User-specified order, displayed exactly as requested in the dropdown
const PROJECT_LINKS = [
  { slug: "nord", label: "NORD" },
  { slug: "monolith", label: "MONOLITH" },
  { slug: "shopdog", label: "SHOP DOG" },
  { slug: "tungsten", label: "TUNGSTEN" },
  { slug: "companeros", label: "COMPAÑEROS" },
  { slug: "wolfram", label: "WOLFRAM" },
];

const EASE = [0.76, 0, 0.24, 1];

export default function Navbar() {
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goToProject = (slug) => {
    setProjectsOpen(false);
    setMobileOpen(false);
    navigate(`/work/${slug}`);
  };

  const goToSection = (id, contactIntent) => {
    setMobileOpen(false);
    if (id === "contact" && contactIntent) announceContactIntent(contactIntent);
    if (location.pathname !== "/") {
      // Use hash so App.js Home hash-effect handles the reliable scroll.
      navigate(`/#${id}`);
      return;
    }
    scrollTo(id);
  };

  // Prevent body scroll when the mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]"
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-10 py-5">
        <button
          data-testid={NAV.logo}
          data-cursor="HOME"
          onClick={() => {
            setMobileOpen(false);
            // Always play the intro. If already on /, just replay in place;
            // otherwise navigate home first so the intro reveals the landing.
            if (location.pathname === "/") {
              window.scrollTo({ top: 0, behavior: "smooth" });
              window.dispatchEvent(new Event("hankdavid-intro"));
            } else {
              navigate("/");
              // Small delay so the route mounts before we replay
              window.setTimeout(() => {
                window.dispatchEvent(new Event("hankdavid-intro"));
              }, 60);
            }
          }}
          className="font-display text-2xl md:text-3xl tracking-tight text-white flex items-baseline gap-3 relative z-[60]"
          aria-label="HANKDAVID, Home"
        >
          <span className="font-bold">HANKDAVID</span>
        </button>

        <nav className="hidden md:flex items-center gap-10 ml-auto mr-10">
          {/* Projects dropdown, replaces the former "Work" + "Approach" items */}
          <div
            className="relative"
            onMouseEnter={() => setProjectsOpen(true)}
            onMouseLeave={() => setProjectsOpen(false)}
          >
            <button
              data-testid={NAV.linkProjects}
              onClick={() => setProjectsOpen((o) => !o)}
              className="mono-label text-white transition-colors flex items-center gap-2"
              data-cursor="↓"
              aria-haspopup="menu"
              aria-expanded={projectsOpen}
            >
              Projects
              <motion.span
                animate={{ rotate: projectsOpen ? 180 : 0 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="inline-block text-[10px] leading-none"
                aria-hidden="true"
              >
                ▾
              </motion.span>
            </button>

            <AnimatePresence>
              {projectsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.28, ease: EASE }}
                  className="absolute right-0 top-full pt-4"
                  role="menu"
                >
                  <div className="min-w-[260px] bg-[#0a0a0b]/95 backdrop-blur-md border border-white/10 shadow-2xl">
                    <ul className="py-3">
                      {PROJECT_LINKS.map((p, i) => (
                        <li key={p.slug}>
                          <motion.button
                            data-testid={NAV.projectItem(p.slug)}
                            data-cursor="OPEN"
                            onClick={() => goToProject(p.slug)}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, ease: EASE, delay: 0.04 + i * 0.03 }}
                            className="w-full text-left px-6 py-3 font-display text-2xl tracking-tight text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-6"
                            role="menuitem"
                          >
                            <span>{p.label}</span>
                            <span aria-hidden="true" className="text-white/30 text-lg">{"\u2197\uFE0E"}</span>
                          </motion.button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button data-testid={NAV.linkStudio} onClick={() => goToSection("studio")} className="mono-label text-white transition-colors" data-cursor="↓">
            Studio
          </button>
          <button data-testid={NAV.linkContact} onClick={() => goToSection("contact", "hello")} className="mono-label text-white transition-colors" data-cursor="→">
            Contact
          </button>
        </nav>

        {/* Desktop CTA */}
        <button
          data-testid={NAV.cta}
          onClick={() => goToSection("contact", "project")}
          data-cursor="INQUIRE"
          className="hidden md:inline-block mono-label border border-white/20 hover:border-white/70 px-4 py-2 text-white transition-colors"
        >
          Begin a Project
        </button>

        {/* Mobile hamburger, thin stack */}
        <button
          data-testid={NAV.mobileMenuToggle}
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="md:hidden relative z-[60] w-8 h-8 flex flex-col items-end justify-center gap-[6px]"
        >
          <motion.span
            animate={mobileOpen ? { rotate: 45, y: 4, width: "22px" } : { rotate: 0, y: 0, width: "22px" }}
            transition={{ duration: 0.35, ease: EASE }}
            className="block h-[1px] bg-white origin-center"
          />
          <motion.span
            animate={mobileOpen ? { opacity: 0, width: "16px" } : { opacity: 1, width: "16px" }}
            transition={{ duration: 0.2, ease: EASE }}
            className="block h-[1px] bg-white"
          />
          <motion.span
            animate={mobileOpen ? { rotate: -45, y: -6, width: "22px" } : { rotate: 0, y: 0, width: "22px" }}
            transition={{ duration: 0.35, ease: EASE }}
            className="block h-[1px] bg-white origin-center"
          />
        </button>
      </div>
      <div className="hairline" />

      {/* Mobile full-screen menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid={NAV.mobileMenuPanel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="md:hidden fixed inset-0 z-40 bg-[#0a0a0b] text-white"
          >
            <div className="h-full flex flex-col px-6 pt-24 pb-10">
              <div className="mono-label text-white/40 mb-8">§ Menu</div>

              <div className="mono-label text-white/40 mb-4">Projects</div>
              <ul className="space-y-3 mb-10">
                {PROJECT_LINKS.map((p, i) => (
                  <li key={p.slug}>
                    <motion.button
                      data-testid={NAV.mobileProjectItem(p.slug)}
                      onClick={() => goToProject(p.slug)}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: EASE, delay: 0.08 + i * 0.04 }}
                      className="font-display text-3xl tracking-tight text-white flex items-center justify-between w-full"
                    >
                      <span>{p.label}</span>
                      <span aria-hidden="true" className="text-white/30 text-lg">{"\u2197\uFE0E"}</span>
                    </motion.button>
                  </li>
                ))}
              </ul>

              <div className="hairline mb-8" />

              <ul className="space-y-4 mb-10">
                <li>
                  <button
                    data-testid={NAV.mobileLinkStudio}
                    onClick={() => goToSection("studio")}
                    className="mono-label text-white/80 hover:text-white transition-colors"
                  >
                    Studio
                  </button>
                </li>
                <li>
                  <button
                    data-testid={NAV.mobileLinkContact}
                    onClick={() => goToSection("contact", "hello")}
                    className="mono-label text-white/80 hover:text-white transition-colors"
                  >
                    Contact
                  </button>
                </li>
              </ul>

              <div className="mt-auto">
                <button
                  data-testid={NAV.mobileCta}
                  onClick={() => goToSection("contact", "project")}
                  className="mono-label border border-white/20 px-5 py-3 w-full text-center hover:border-white/70 transition-colors"
                >
                  Begin a Project →
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
